import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { updateQuantity } from '../redux/slices/cartSlice';
import { generateCartItemId } from '../utils/cartUtils';

/**
 * Reusable hook for combo quantity controls
 * Provides simple increment/decrement without modals
 */
export const useComboQuantityControls = (comboId: string) => {
  const dispatch = useAppDispatch();
  const { items: cartItems } = useAppSelector(state => state.cart);
  
  // Generate correct cart item ID matching the format used when adding to cart
  // Format: {comboId}_none_no-mods (no spice, no modifiers)
  const cartItemId = useMemo(() => 
    generateCartItemId(comboId, 'none', []),
    [comboId]
  );
  
  // Get current quantity in cart for this combo
  const cartQuantity = useMemo(() => {
    const item = cartItems.find(item => item.cartItemId === cartItemId);
    return item ? item.quantity : 0;
  }, [cartItemId, cartItems]);
  
  // Direct increment (adds 1)
  const handleIncrement = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    dispatch(updateQuantity({ 
      cartItemId, 
      quantity: cartQuantity + 1 
    }));
  }, [dispatch, cartItemId, cartQuantity]);
  
  // Direct decrement (removes 1, auto-removes at 0)
  const handleDecrement = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    dispatch(updateQuantity({ 
      cartItemId, 
      quantity: cartQuantity - 1 
    }));
  }, [dispatch, cartItemId, cartQuantity]);
  
  return {
    cartQuantity,
    handleIncrement,
    handleDecrement,
  };
};

export default useComboQuantityControls;
